import { Box, Input, Typography, useTheme } from '@mui/material';
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import VideosIcon from '../../../../components/Icons/VideosIcon';
import CustomButton from '../../../../components/MUIComponents/CustomButton';
import IconRoundBackground from '../../../../components/MUIComponents/IconRoundBackground';
import SquareIconButton from '../../../../components/MUIComponents/SquareIconButton';
import StandardImageList from '../../../../components/MUIComponents/StandardImageList';
import { ButtonTypeEnum } from '../../../../enums/button-type-enum';
import {
  useAddUserVideoMutation,
  useGetAllUserVideosByUserIdQuery,
  useGetAllUserVideosQuery,
} from '../../../../store/apiSlice/profileMediaApi';
import { colors } from '../../../../theme/theme';
import { ElementIdEnum } from '../../../../enums/element-id-enum';
import { SnackbarSeverityEnum } from '../../../../enums/snackbar-severity-enum';
import { isSupportedIdConfirmationVideoFileHelper } from '../../../../utils/helper/file-validation-helper';
import AddVideosModal from '../../../../components/Media/Modals/AddVideosModal';
import { useModal } from '../../../../hooks/useModal';
import GridSkeleton from '../../../../components/Media/GridSkeleton';
import MediaPreviewModal from '../../../../components/Media/Modals/MediaPreviewModal';
import ButtonsOverlay from '../../../../components/Media/Layouts/ButtonsOverlay';

interface VideosProps {
  navigateVideos: () => void;
  setSnackbarOpen: Dispatch<SetStateAction<boolean>>;
  setSnackbarMessage: Dispatch<SetStateAction<string>>;
  setSnackbarSeverity: Dispatch<SetStateAction<SnackbarSeverityEnum>>;
}

const Videos = ({
  navigateVideos,
  setSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
}: VideosProps) => {
  const { id } = useParams();
  const theme = useTheme();
  const { isOpen, openModal, closeModal } = useModal();
  const { isOpen: isOpenMediaModal, toggleModal: toggleMediaModal } =
    useModal();
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const getAllUserVideosQueryResult = useGetAllUserVideosQuery(
    {
      page: 1,
      limit: 6,
    },
    {
      skip: !!id,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const getAllVideosByUserIdQueryResult = useGetAllUserVideosByUserIdQuery(
    {
      id: id || '',
      page: 1,
      limit: 6,
    },
    {
      skip: !id,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: videoData, isLoading } = id
    ? getAllVideosByUserIdQueryResult
    : getAllUserVideosQueryResult;

  const videos = useMemo(
    () =>
      videoData?.data.map((item) => ({
        src: item.videoThumbnail
          ? `data:image/png;base64,${item.videoThumbnail}`
          : '',
        srcSet: item.videoThumbnail
          ? `data:image/png;base64,${item.videoThumbnail}`
          : '',
        title: item.fileName ?? '',
      })),
    [videoData]
  );

  const videosForPreview = useMemo(
    () =>
      videoData?.data.map((item) => ({
        id: item.id,
        fileName: item.fileName ?? '',
        fileUrl: item.fileUrl ?? '',
      })),
    [videoData]
  );

  const [addUserVideo, { isLoading: isUpdatingFiles }] =
    useAddUserVideoMutation();

  const addFile = async (files: FileList) => {
    const newFiles = Array.from(files);
    const validFiles = newFiles.filter((file) =>
      isSupportedIdConfirmationVideoFileHelper(file)
    );
    const invalidFiles = newFiles.filter(
      (file) => !isSupportedIdConfirmationVideoFileHelper(file)
    );

    if (validFiles.length > 5) {
      setSnackbarMessage('You can upload up to 5 files at once');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }

    if (invalidFiles.length > 0) {
      setSnackbarMessage('Invalid file type. Only videos (mp4, ...)');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      return;
    }
    try {
      await Promise.all(
        validFiles.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          await addUserVideo(formData).unwrap();
        })
      );
      setSnackbarMessage('All files uploaded successfully');
      setSnackbarSeverity(SnackbarSeverityEnum.SUCCESS);
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(`Error uploading files: ${error}`);
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      console.error('Error uploading files:', error);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFile(event.target.files);
      event.target.value = '';
    }
  };

  const handleOpenMediaPreview = (index: number) => {
    setCurrentIndex(index);
    toggleMediaModal();
  };

  const handleNext = () => {
    if (!videosForPreview) return;
    setCurrentIndex((prevIndex) =>
      prevIndex < videosForPreview.length - 1 ? prevIndex + 1 : 0
    );
  };

  const handlePrev = () => {
    if (!videosForPreview) return;
    setCurrentIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : videosForPreview.length - 1
    );
  };

  const mainContent = (
    <>
      {!!videos?.length && (
        <Box className="pfofile-videos" marginTop="22px">
          <StandardImageList
            itemData={videos || []}
            imageListProps={{
              cols: 3,
              sx: {
                width: '100%',
                height: { lg: 134, xl: 185 },
                overflow: 'hidden',
                gap: { lg: '6px !important' },
              },
            }}
            imageListItemProps={{
              sx: {
                [theme.breakpoints.up('lg')]: {
                  width: 64,
                  height: '64px !important',
                },
                [theme.breakpoints.up('xl')]: {
                  width: 90,
                  height: '90px !important',
                },
              },
            }}
            overlayButtons={
              <ButtonsOverlay
                isVideo
                nameOfClass={'video-buttons-overlay'}
                openHandler={() => handleOpenMediaPreview(currentIndex)}
                removeHandler={() => {}}
                hasRemove={false}
              />
            }
          />
          <MediaPreviewModal
            isVideo
            files={videosForPreview!}
            currentIndex={currentIndex}
            isOpen={isOpenMediaModal}
            onClose={() => toggleMediaModal()}
            clickNext={handleNext}
            clickPrev={handlePrev}
            deleteFile={() => {}}
          />
        </Box>
      )}
      {!videos?.length && (
        <Box
          className="upload_videos"
          width="100%"
          height="100%"
          minHeight={186}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          rowGap="18px"
          marginTop="22px"
          sx={(theme) => ({ bgcolor: theme.palette.secondary2.main })}
        >
          <IconRoundBackground
            backgroundColor={theme.palette.background.lightDark}
          >
            <VideosIcon />
          </IconRoundBackground>

          {id && (
            <Typography
              variant="body3"
              color="black"
              sx={{ fontWeight: '500 !important' }}
            >
              no videos
            </Typography>
          )}

          {!id && (
            <>
              <Typography
                variant="body3"
                color="black"
                sx={{ fontWeight: '500 !important' }}
              >
                Upload video
              </Typography>
              <CustomButton
                variant="outlined"
                variantType={ButtonTypeEnum.PRIMARY}
                onClick={openModal}
                disabled={isLoading || isUpdatingFiles}
                fontSize={'0.625rem'}
                sx={{ p: '0.8125rem 1rem', height: 40 }}
              >
                choose files
              </CustomButton>
              <Box
                component="input"
                type="file"
                id="videoFileInput"
                accept="video/*"
                display="none"
              />
            </>
          )}
        </Box>
      )}
    </>
  );

  return (
    <Box
      className="profile-videos-box"
      width={150}
      sx={{
        height: 'fit-content',
        width: '100%',
      }}
    >
      <Box
        className="profile-videos-title"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            color={colors.fontDark}
          >
            VIDEOS
          </Typography>
        </Box>
        <Box>
          <SquareIconButton
            onClick={navigateVideos}
            sx={{ p: 0, minWidth: 32, minHeight: 32, width: 30, height: 30 }}
          >
            <VideosIcon />
          </SquareIconButton>
        </Box>
      </Box>
      {isLoading || isUpdatingFiles ? (
        <Box sx={{ marginTop: '22px' }}>
          <GridSkeleton lg={4} xl={4} itemCount={6} />
        </Box>
      ) : (
        mainContent
      )}
      <Input
        id={ElementIdEnum.ADD_VIDEOS_SIDEBAR_BUTTON}
        type="file"
        sx={{ display: 'none' }}
        inputProps={{ multiple: true, accept: 'video/*' }}
        onChange={handleFileChange}
      />
      <AddVideosModal
        isOpen={isOpen}
        onClose={closeModal}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarSeverity={setSnackbarSeverity}
      />
    </Box>
  );
};

export default Videos;
